import React, { useState, useEffect } from 'react';
import axios from 'axios';

interface WhatsAppCommunication {
  id: number;
  date: string;
  sent_to: string;
  message: string;
  employee: string;
  delivery_status: 'sent' | 'delivered' | 'failed' | 'pending';
  ref_no: string;
}

interface ClientClaimWhatsAppTabProps {
  clientId: string;
  claimId: string;
  claimRef: string;
  clientName?: string;
  policyNumber?: string;
  user?: {
    id?: number;
    username?: string;
    name?: string;
    email?: string;
  };
}

interface ClaimDocument {
  id: number;
  file_name: string;
  original_name: string;
  file_path: string;
  description: string;
  created_at: string;
  file_size: number;
  document_type: string;
}

interface PersonOfInterest {
  id: number;
  name: string;
  surname: string;
  contact_number: string;
}

// Helper function to generate a reference number
const generateRefNo = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');
  const random = String(Math.floor(Math.random() * 10000)).padStart(4, '0');
  
  return `REF-${year}${month}${day}-${hours}${minutes}${seconds}-${random}`;
};

const ClientClaimWhatsAppTab: React.FC<ClientClaimWhatsAppTabProps> = ({ 
  clientId,
  claimId,
  claimRef,
  clientName = '',
  policyNumber = '',
  user
}) => {
  const [whatsappMessages, setWhatsAppMessages] = useState<WhatsAppCommunication[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loggedInUser, setLoggedInUser] = useState<string>('System');
  const [selectedMessage, setSelectedMessage] = useState<WhatsAppCommunication | null>(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  // Contact options for the dropdowns
  const [clientPhoneNumber, setClientPhoneNumber] = useState<string>('');
  const [personsOfInterest, setPersonsOfInterest] = useState<PersonOfInterest[]>([]);
  const [isLoadingContacts, setIsLoadingContacts] = useState(false);

  const [formData, setFormData] = useState({
    send_to_client: '',
    send_to_poi: '',
    message: '',
  });

  const [recipients, setRecipients] = useState<string[]>([]);
  const [selectedDocuments, setSelectedDocuments] = useState<number[]>([]);
  const [claimDocuments, setClaimDocuments] = useState<ClaimDocument[]>([]);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);

  useEffect(() => {
    fetchWhatsAppMessages();
    fetchContacts();
    fetchClaimDocuments();
  }, [clientId, claimId]);

  // Get logged-in user
  useEffect(() => {
    if (user) {
      const userName = user.username || user.name || user.email;
      if (userName) {
        setLoggedInUser(userName);
        return;
      }
    }
    
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        const userName = userData.username || userData.name || userData.email;
        if (userName) {
          setLoggedInUser(userName);
          return;
        }
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }
    
    setLoggedInUser('System');
  }, [user]);

  // Clear documents when claimId changes
  useEffect(() => {
    if (claimId) {
      setClaimDocuments([]);
      setSelectedDocuments([]);
    }
  }, [claimId]);

  const fetchWhatsAppMessages = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication required');
        setIsLoading(false);
        return;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/whatsapp-claims/communications/${claimId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setWhatsAppMessages(response.data.data || response.data || []);
    } catch (err) {
      console.error('Error fetching WhatsApp communications:', err);
      setError('Failed to load WhatsApp communications');
      setWhatsAppMessages([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchContacts = async () => {
    try {
      setIsLoadingContacts(true);
      const token = localStorage.getItem('token');
      if (!token) return;

      // Fetch client's phone number
      try {
        const clientResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/clients/${clientId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const clientData = clientResponse.data.data || clientResponse.data;
        if (clientData.mobile_number || clientData.contact_number || clientData.phone) {
          setClientPhoneNumber(clientData.mobile_number || clientData.contact_number || clientData.phone);
        }
      } catch (err) {
        console.warn('Could not fetch client phone number:', err);
      }

      // Fetch persons of interest with phone numbers
      try {
        const poiResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/persons-of-interest?clientId=${clientId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        const persons = Array.isArray(poiResponse.data) ? poiResponse.data : poiResponse.data.data || [];
        const personsWithPhone = persons.filter((p: any) => p.contact_number);
        console.log(`✅ Found ${personsWithPhone.length} persons of interest with phone numbers`);
        setPersonsOfInterest(personsWithPhone);
      } catch (err) {
        console.warn('Could not fetch persons of interest:', err);
      }
    } catch (err) {
      console.error('Error fetching contacts:', err);
    } finally {
      setIsLoadingContacts(false);
    }
  };

  const fetchClaimDocuments = async () => {
    try {
      setIsLoadingDocuments(true);
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('No token found for fetching claim documents');
        setIsLoadingDocuments(false);
        return;
      }

      console.log(`📄 Fetching claim documents for ID: ${claimId}`);
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/claim-documents/${claimId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const documents = Array.isArray(response.data) ? response.data : response.data.data || [];
      console.log(`✅ Found ${documents.length} claim documents`);
      
      const mappedDocuments = documents.map((doc: any) => ({
        id: doc.id,
        file_name: doc.file_name || doc.original_name || 'document.pdf',
        original_name: doc.original_name || doc.file_name || 'document.pdf',
        file_path: doc.file_path || '',
        description: doc.description || '',
        created_at: doc.created_at || new Date().toISOString(),
        file_size: doc.file_size || 0,
        document_type: doc.document_type || 'claim'
      }));
      
      setClaimDocuments(mappedDocuments);
    } catch (err) {
      console.warn('Could not fetch claim documents:', err);
      setClaimDocuments([]);
    } finally {
      setIsLoadingDocuments(false);
    }
  };

  // Handle adding a recipient from client number
  const handleAddClientRecipient = () => {
    const phone = formData.send_to_client.trim();
    if (!phone) {
      setError('Please select a client phone number');
      return;
    }
    if (recipients.includes(phone)) {
      setError('This phone number is already added');
      return;
    }
    setRecipients([...recipients, phone]);
    setFormData(prev => ({ ...prev, send_to_client: '' }));
    setError(null);
  };

  // Handle adding a recipient from POI
  const handleAddPoiRecipient = () => {
    const phone = formData.send_to_poi.trim();
    if (!phone) {
      setError('Please select a person of interest');
      return;
    }
    if (recipients.includes(phone)) {
      setError('This phone number is already added');
      return;
    }
    setRecipients([...recipients, phone]);
    setFormData(prev => ({ ...prev, send_to_poi: '' }));
    setError(null);
  };

  // Handle removing a recipient
  const handleRemoveRecipient = (phone: string) => {
    setRecipients(recipients.filter(r => r !== phone));
  };

  // Handle document toggle
  const handleDocumentToggle = (docId: number) => {
    setSelectedDocuments(prev => {
      if (prev.includes(docId)) {
        return prev.filter(id => id !== docId);
      } else {
        return [...prev, docId];
      }
    });
  };

  // Handle view message
  const handleViewMessage = (message: WhatsAppCommunication) => {
    setSelectedMessage(message);
    setShowPreviewModal(true);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Format phone number for WhatsApp
  const formatPhoneForWhatsApp = (phoneNumber: string): string | null => {
    if (!phoneNumber) return null;

    // Remove all non-digit characters except +
    let cleanNumber = phoneNumber.replace(/[^\d+]/g, '');
    
    // Handle different formats
    if (cleanNumber.startsWith('+')) {
      cleanNumber = cleanNumber.substring(1);
    } else if (cleanNumber.startsWith('0') && cleanNumber.length === 10) {
      cleanNumber = '27' + cleanNumber.substring(1);
    } else if (!cleanNumber.startsWith('27') && cleanNumber.length > 9) {
      cleanNumber = '27' + cleanNumber;
    }

    // Validate the final number
    if (cleanNumber.length !== 11 || !cleanNumber.startsWith('27')) {
      console.error('❌ Invalid phone number format after cleaning:', cleanNumber);
      return null;
    }

    return cleanNumber;
  };

  // Get client name
  const getClientName = async (): Promise<string> => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/clients/${clientId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const clientData = response.data.data || response.data;
      return `${clientData.name || ''} ${clientData.surname || ''}`.trim() || 'Client';
    } catch (error) {
      return 'Client';
    }
  };

  const handleSendWhatsApp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (recipients.length === 0) {
      setError('Please add at least one recipient');
      return;
    }
    if (!formData.message.trim()) {
      setError('Message is required');
      return;
    }

    const generatedRefNo = generateRefNo();

    try {
      setIsSubmitting(true);
      setError(null);
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication required');
        setIsSubmitting(false);
        return;
      }

      // Get the selected document details
      const selectedDocs = claimDocuments.filter(doc => 
        selectedDocuments.includes(doc.id)
      );

      // Build the message with file attachments
      let fullMessage = formData.message.trim();
      
      // Add claim reference to the message
      fullMessage = `Claim: ${claimRef}\n\n${fullMessage}`;
      
      // Add client info if available
      if (clientName) {
        fullMessage = `Client: ${clientName}\n${fullMessage}`;
      }
      
      // Add file information to the message
      if (selectedDocs.length > 0) {
        fullMessage += '\n\n📎 *Attached Files:*\n';
        selectedDocs.forEach((doc, index) => {
          const fileName = doc.original_name || doc.file_name || 'Document';
          fullMessage += `${index + 1}. ${fileName}\n`;
        });
        fullMessage += '\n';
      }

      // Save the communication record via API
      const payload = {
        to: recipients,
        message: fullMessage,
        client_id: parseInt(clientId),
        claim_id: parseInt(claimId),
        ref_no: generatedRefNo,
        employee: loggedInUser,
        document_ids: selectedDocuments,
        documents: selectedDocs.map(doc => ({
          id: doc.id,
          file_name: doc.file_name,
          original_name: doc.original_name,
          file_path: doc.file_path
        }))
      };

      console.log('📧 Saving WhatsApp record with payload:', payload);

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/whatsapp-claims/send`,
        payload,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          timeout: 30000
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to save WhatsApp record');
      }

      console.log('✅ WhatsApp record saved:', response.data);

      // Add the new message to the list
      const newMessage = {
        id: Date.now(),
        date: new Date().toISOString(),
        sent_to: recipients.join(', '),
        message: fullMessage,
        employee: loggedInUser,
        delivery_status: 'sent' as WhatsAppCommunication['delivery_status'],
        ref_no: generatedRefNo
      };
      setWhatsAppMessages([newMessage, ...whatsappMessages]);

      // ✅ OPEN WHATSAPP WEB/APP WITH PRE-FILLED MESSAGE
      const firstRecipient = recipients[0];
      const formattedPhone = formatPhoneForWhatsApp(firstRecipient);
      
      if (!formattedPhone) {
        setError('Invalid phone number format');
        setIsSubmitting(false);
        return;
      }

      // Get client name
      const clientNameForMessage = await getClientName();
      
      // Build the WhatsApp message with clickable document links
      let whatsappMessage = `Hello ${clientNameForMessage},\n\n`;
      whatsappMessage += `Claim: ${claimRef}\n\n`;
      whatsappMessage += formData.message.trim();
      
      // Add document links with public tokens
      if (selectedDocs.length > 0) {
        whatsappMessage += '\n\n📎 *Attached Files:*\n';
        
        const baseUrl = process.env.REACT_APP_FRONTEND_URL || 'http://qbx.co.za';
        const authToken = localStorage.getItem('token');

        for (const doc of selectedDocs) {
          const fileName = doc.original_name || doc.file_name || 'Document';
          
          try {
            console.log('🔑 Generating public token for file:', doc.id);
            
            // ✅ Call backend to generate public token
            const tokenResponse = await axios.post(
              `${process.env.REACT_APP_API_URL}/api/public-token/generate/${doc.id}`,
              {},
              { 
                headers: { 
                  'Authorization': `Bearer ${authToken}`,
                  'Content-Type': 'application/json'
                } 
              }
            );
            
            console.log('✅ Public token response:', tokenResponse.data);
            
            const publicToken = tokenResponse.data.data?.token;
            
            if (publicToken) {
              const fileUrl = `${baseUrl}/api/public-token/view/${publicToken}`;
              whatsappMessage += `\n${fileName}\n${fileUrl}\n`;
              console.log('✅ Added public link with token:', fileUrl);
            } else {
              console.error('❌ No token in response');
              whatsappMessage += `\n${fileName}\nNo link available\n`;
            }
          } catch (err: any) {
            console.error('❌ Failed to generate public token for file:', doc.id, err.response?.data || err.message);
            whatsappMessage += `\n${fileName}\nPlease request the file from your consultant.\n`;
          }
        }
      }

      // Add a note about the links
      if (selectedDocs.length > 0) {
        whatsappMessage += `\n💡 Tap the links above to view or download the files.`;
      }

      // ✅ ENCODE THE MESSAGE PROPERLY FOR URL
      const encodedMessage = encodeURIComponent(whatsappMessage);
      
      // Use WhatsApp Web URL with properly encoded message
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      const whatsappUrl = isMobile 
        ? `https://api.whatsapp.com/send?phone=${formattedPhone}&text=${encodedMessage}`
        : `https://web.whatsapp.com/send?phone=${formattedPhone}&text=${encodedMessage}`;
      
      console.log('🔗 Opening WhatsApp URL with encoded message:', whatsappUrl);
      
      // Open WhatsApp in a new tab
      const newWindow = window.open(whatsappUrl, '_blank');
      if (newWindow) {
        newWindow.focus();
      }

      setError(null);
      resetForm();
      setShowCreateModal(false);

      const fileCount = selectedDocs.length;
      const fileMessage = fileCount > 0 ? ` with ${fileCount} file(s) (clickable links included)` : '';
      alert(`✅ WhatsApp message prepared for ${recipients.length} recipient(s)${fileMessage}. Please click send in WhatsApp.`);

    } catch (err: any) {
      console.error('Error saving WhatsApp:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to save WhatsApp message';
      setError(errorMessage);
      alert(`❌ ${errorMessage}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      send_to_client: '',
      send_to_poi: '',
      message: '',
    });
    setRecipients([]);
    setSelectedDocuments([]);
  };

  const handleCloseModal = () => {
    resetForm();
    setShowCreateModal(false);
    setError(null);
    setSelectedDocuments([]);
  };

  const getStatusBadgeClass = (status: WhatsAppCommunication['delivery_status']) => {
    const classes = {
      sent: 'bg-blue-100 text-blue-800',
      delivered: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      pending: 'bg-yellow-100 text-yellow-800'
    };
    return classes[status] || classes.pending;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-32">
        <div className="text-gray-500">Loading WhatsApp communications...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">WhatsApp Communications</h3>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
          Send WhatsApp
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {whatsappMessages.length === 0 ? (
        <div className="text-gray-500 text-center py-8">
          No WhatsApp communications found for this claim.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sent To
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Employee
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Delivery Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ref No
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {whatsappMessages.map((message) => (
                <tr key={message.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(message.date).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {message.sent_to}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {message.employee}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(message.delivery_status)}`}>
                      {message.delivery_status.charAt(0).toUpperCase() + message.delivery_status.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {message.ref_no || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <button
                      onClick={() => handleViewMessage(message)}
                      className="text-green-600 hover:text-green-800 font-medium text-sm transition-colors flex items-center gap-1"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* WhatsApp Message Preview Modal */}
      {showPreviewModal && selectedMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl mx-4 max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">WhatsApp Message Preview</h3>
              <button
                onClick={() => {
                  setShowPreviewModal(false);
                  setSelectedMessage(null);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              {/* Message Header */}
              <div className="bg-green-50 p-4 rounded-md space-y-2">
                <div className="flex items-start">
                  <span className="text-sm font-medium text-gray-500 w-24">To:</span>
                  <span className="text-sm text-gray-900 flex-1">{selectedMessage.sent_to}</span>
                </div>
                <div className="flex items-start">
                  <span className="text-sm font-medium text-gray-500 w-24">Date:</span>
                  <span className="text-sm text-gray-900 flex-1">
                    {new Date(selectedMessage.date).toLocaleString()}
                  </span>
                </div>
                <div className="flex items-start">
                  <span className="text-sm font-medium text-gray-500 w-24">Employee:</span>
                  <span className="text-sm text-gray-900 flex-1">{selectedMessage.employee}</span>
                </div>
                <div className="flex items-start">
                  <span className="text-sm font-medium text-gray-500 w-24">Ref No:</span>
                  <span className="text-sm text-gray-900 flex-1 font-mono">{selectedMessage.ref_no || '-'}</span>
                </div>
                <div className="flex items-start">
                  <span className="text-sm font-medium text-gray-500 w-24">Status:</span>
                  <span className="text-sm flex-1">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(selectedMessage.delivery_status)}`}>
                      {selectedMessage.delivery_status.charAt(0).toUpperCase() + selectedMessage.delivery_status.slice(1)}
                    </span>
                  </span>
                </div>
              </div>

              {/* Message Body */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Message:</h4>
                <div className="bg-white border border-gray-200 rounded-md p-4 whitespace-pre-wrap text-sm text-gray-800 min-h-[100px] max-h-[300px] overflow-y-auto">
                  {selectedMessage.message || '-'}
                </div>
              </div>

              {/* WhatsApp Icon */}
              <div className="flex justify-center">
                <div className="bg-green-100 p-3 rounded-full">
                  <svg className="w-8 h-8 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                </div>
              </div>
            </div>

            <div className="flex justify-end p-6 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowPreviewModal(false);
                  setSelectedMessage(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">Send WhatsApp Message</h3>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleSendWhatsApp} className="p-6 space-y-4">
              {/* Claim Reference Display */}
              <div className="bg-blue-50 p-3 rounded-md border border-blue-200">
                <p className="text-sm text-blue-700">
                  <span className="font-semibold">Claim:</span> {claimRef}
                </p>
              </div>

              {/* Send to Client Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Send to Client Number
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Select the client's phone number and click Add
                </p>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <select
                      value={formData.send_to_client}
                      onChange={(e) => setFormData({ ...formData, send_to_client: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 appearance-none bg-white"
                      disabled={!clientPhoneNumber}
                    >
                      <option value="">
                        {clientPhoneNumber ? 'Select client phone number...' : 'No client phone number found'}
                      </option>
                      {clientPhoneNumber && (
                        <option value={clientPhoneNumber}>
                          Client: {clientPhoneNumber}
                        </option>
                      )}
                    </select>
                  </div>
                  <button
                    type="button"
                    onClick={handleAddClientRecipient}
                    disabled={!formData.send_to_client}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                      formData.send_to_client
                        ? 'bg-blue-500 text-white hover:bg-blue-600'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    Add
                  </button>
                </div>
              </div>

              {/* Send to Person of Interest Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Send to Person of Interest Number
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Select a person of interest's phone number and click Add
                </p>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <select
                      value={formData.send_to_poi}
                      onChange={(e) => setFormData({ ...formData, send_to_poi: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 appearance-none bg-white"
                      disabled={personsOfInterest.length === 0}
                    >
                      <option value="">
                        {personsOfInterest.length > 0 ? 'Select a person of interest...' : 'No persons of interest with phone numbers'}
                      </option>
                      {personsOfInterest.map((person) => (
                        <option key={person.id} value={person.contact_number}>
                          {person.name} {person.surname}: {person.contact_number}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button
                    type="button"
                    onClick={handleAddPoiRecipient}
                    disabled={!formData.send_to_poi}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                      formData.send_to_poi
                        ? 'bg-blue-500 text-white hover:bg-blue-600'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    Add
                  </button>
                </div>
              </div>

              {/* Recipients List */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Recipients ({recipients.length})
                </label>
                {recipients.length === 0 ? (
                  <div className="p-3 border border-gray-200 rounded-md bg-gray-50 text-gray-400 text-sm">
                    No recipients added yet. Add client or POI phone numbers above.
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2 p-3 border border-gray-200 rounded-md bg-gray-50">
                    {recipients.map((phone, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm"
                      >
                        {phone}
                        <button
                          type="button"
                          onClick={() => handleRemoveRecipient(phone)}
                          className="ml-1 text-green-600 hover:text-green-800 focus:outline-none"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Message Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message *
                </label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  rows={4}
                  placeholder="WhatsApp message content..."
                  required
                />
              </div>

              {/* Select Files Section */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Files
                </label>
                {isLoadingDocuments ? (
                  <div className="text-gray-500 text-sm py-2">Loading documents...</div>
                ) : claimDocuments.length === 0 ? (
                  <div className="text-gray-500 text-sm py-2">No documents found for this claim.</div>
                ) : (
                  <div className="border border-gray-300 rounded-md p-3 max-h-48 overflow-y-auto">
                    {claimDocuments.map((doc) => (
                      <label key={doc.id} className="flex items-start space-x-3 py-2 hover:bg-gray-50 px-2 rounded cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedDocuments.includes(doc.id)}
                          onChange={() => handleDocumentToggle(doc.id)}
                          className="mt-1 h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {doc.original_name || doc.file_name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {doc.document_type || 'Document'} • {formatFileSize(doc.file_size || 0)}
                            {doc.created_at && ` • ${new Date(doc.created_at).toLocaleDateString()}`}
                          </p>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
                {selectedDocuments.length > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    {selectedDocuments.length} file(s) selected
                  </p>
                )}
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                  {error}
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-500 hover:bg-green-600 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Sending...' : 'Send WhatsApp'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientClaimWhatsAppTab;